/**
 * Everything the comparison screen shows, derived from one diff.
 *
 * A pure module rather than a body of `useMemo`s in the screen: the rules for
 * what counts as a substantive change are the interesting part, and they are
 * testable without rendering anything.
 *
 * @module components/portfolio/comparison/comparison-model
 */

import {
  academicChanges,
  academicMappingChanges,
} from "@/components/portfolio/comparison/academic-changes";
import {
  COMPARISON_AREA_LABELS,
  type ComparisonArea,
  matchesComparisonArea,
} from "@/components/portfolio/comparison/comparison-areas";
import type {
  AcademicChange,
  MappingDifference,
  ModuleDifference,
} from "@/components/portfolio/comparison/diff-types";
import { compareMappings } from "@/components/portfolio/comparison/mapping-differences";
import { compareModules } from "@/components/portfolio/comparison/module-differences";
import { isTechnicalRevisionChange } from "@/components/shared/diff-labels";
import type { RevisionComparison } from "@/server/governance-diff";
import type { CurriculumRevision, ProgrammeIdentityFieldChange } from "@/server/types";
import { type AssessmentTypeSlice, buildAssessmentTypeDistribution } from "@/utils/assessments";

export interface ComparisonAreaCount {
  id: ComparisonArea;
  label: string;
  count: number;
}

export interface ComparisonModel {
  modules: ModuleDifference[];
  changedModules: ModuleDifference[];
  mappings: MappingDifference[];
  /** What a reader is here for: the curriculum changed, minus the plumbing. */
  substantiveChanges: AcademicChange[];
  /** System metadata differences, kept for audit and shown apart. */
  technicalChanges: AcademicChange[];
  /** Every projected change, keyed `path:kind`, for a module row to look up. */
  projectedChangesById: Map<string, AcademicChange>;
  identityChanges: readonly ProgrammeIdentityFieldChange[];
  /** Only the areas carrying a change, so the overview offers no dead filters. */
  areas: ComparisonAreaCount[];
  earlierAssessmentProfile: AssessmentTypeSlice[];
  laterAssessmentProfile: AssessmentTypeSlice[];
}

/** What the screen shows before a pair has been compared. */
export const EMPTY_COMPARISON_MODEL: ComparisonModel = {
  modules: [],
  changedModules: [],
  mappings: [],
  substantiveChanges: [],
  technicalChanges: [],
  projectedChangesById: new Map(),
  identityChanges: [],
  areas: [],
  earlierAssessmentProfile: [],
  laterAssessmentProfile: [],
};

/**
 * Derive everything the screen renders from one comparison.
 *
 * @param fromRevision - The earlier approved revision.
 * @param toRevision - The later one.
 * @param comparison - The server's diff of the two.
 * @returns The model, or the empty one when any input is absent.
 */
export function buildComparisonModel(
  fromRevision: CurriculumRevision | undefined,
  toRevision: CurriculumRevision | undefined,
  comparison: RevisionComparison | null,
): ComparisonModel {
  if (!fromRevision || !toRevision || !comparison) {
    return EMPTY_COMPARISON_MODEL;
  }
  const before = fromRevision.data;
  const after = toRevision.data;
  const modules = compareModules(before, after, comparison.changes);
  const mappings = compareMappings(before, after);
  const projected = academicChanges(before, after, comparison.changes);
  const technicalIds = new Set(
    comparison.changes
      .filter(isTechnicalRevisionChange)
      .map((change) => `${change.path}:${change.kind}`),
  );
  const substantiveChanges = [
    // Mapping changes are re-derived from the two documents rather than taken
    // from the diff: the diff records `mimloIds` arrays, which say nothing a
    // reader can act on.
    ...projected.filter(
      (change) =>
        change.category !== "mappings" &&
        !change.path.endsWith(".mimloIds") &&
        !technicalIds.has(change.id),
    ),
    ...academicMappingChanges(mappings),
  ];

  return {
    modules,
    changedModules: modules.filter((module) => !module.statuses.includes("unchanged")),
    mappings,
    substantiveChanges,
    technicalChanges: projected.filter((change) => technicalIds.has(change.id)),
    projectedChangesById: new Map(projected.map((change) => [change.id, change])),
    identityChanges: comparison.identityChanges ?? [],
    areas: (Object.keys(COMPARISON_AREA_LABELS) as ComparisonArea[])
      .filter((area) => area !== "all")
      .map((area) => ({
        id: area,
        label: COMPARISON_AREA_LABELS[area],
        count: substantiveChanges.filter((change) => matchesComparisonArea(change, area)).length,
      }))
      .filter((area) => area.count > 0),
    earlierAssessmentProfile: buildAssessmentTypeDistribution(before),
    laterAssessmentProfile: buildAssessmentTypeDistribution(after),
  };
}
